import { decodePolicyMutation } from '../lib/policy-authoring'
import { decodeBrand } from '@/brand/decode'
import type { Brand } from '@/brand/types'
import type { components } from '@/lib/openapi.generated'
import {
  isRecord,
  json,
  nullableString,
  request,
  requireBoolean,
  requireNumber,
  requireString,
  withQuery,
} from '@/lib/api'

type Schemas = components['schemas']
export type BrandingPolicy = Omit<Schemas['BrandingPolicyResponse'], 'value' | 'selectorGui'> & {
  value: Brand
  selectorGui?: Record<string, unknown> | null
}
export type DefineWhitelabelSettings = Omit<Schemas['SetWhitelabelSettingsRequest'], 'mode' | 'value' | 'operationId'> & {
  mode: 'Define'
  value: Brand
  operationId: string
}
export type WhitelabelSettingsWrite = DefineWhitelabelSettings
  | (Pick<Schemas['SetWhitelabelSettingsRequest'], 'expectedDefinition'> & { mode: 'Inherit' })
type WhitelabelSettingsResult = { mode: 'Define'; mutation: Schemas['BrandingPolicyMutationResponse'] }
  | { mode: 'Inherit' }

function decodePolicy(raw: unknown): BrandingPolicy {
  if (!isRecord(raw)) throw new Error('Malformed branding policy')
  return {
    id: requireString(raw.id, 'id'),
    name: requireString(raw.name, 'name'),
    value: decodeBrand(raw.value),
    selectorExpression: nullableString(raw.selectorExpression, 'selectorExpression'),
    selectorGui: decodeSelectorGui(raw.selectorGui),
    priority: requireNumber(raw.priority, 'priority'),
    revision: requireNumber(raw.revision, 'revision'),
  } satisfies BrandingPolicy
}

function decodeSelectorGui(raw: unknown): Record<string, unknown> | null {
  if (raw === undefined || raw === null) return null
  if (!isRecord(raw)) throw new Error('Malformed policy selector UI state')
  return raw
}

const decodeMutation = (raw: unknown) =>
  decodePolicyMutation(raw) satisfies Schemas['BrandingPolicyMutationResponse']

function decodeSettingsResult(raw: unknown, mode: WhitelabelSettingsWrite['mode']): WhitelabelSettingsResult {
  if (!isRecord(raw) || raw.mode !== mode) throw new Error('Malformed whitelabel settings response')
  if (mode === 'Inherit') {
    if (raw.mutation !== null && !decodeMutation(raw.mutation).deleted)
      throw new Error('Malformed whitelabel inheritance response')
    return { mode: 'Inherit' }
  }
  const mutation = decodeMutation(raw.mutation)
  if (mutation.deleted) throw new Error('Malformed whitelabel definition response')
  return { mode: 'Define', mutation }
}

export const WHITELABEL_POLICY_TYPE = 'entity:whitelabel.config'

const policiesPath = (entityId: string) => `/api/v1/entities/${entityId}/branding/policies`
const policyPath = (entityId: string, policyId: string) => `${policiesPath(entityId)}/${policyId}`

export class WhitelabelMigrationRequiredError extends Error {
  constructor() {
    super('Multiple whitelabel definitions need an administrator to finish the upgrade before editing.')
  }
}

export const whitelabelPolicyApi = {
  own: (entityId: string, signal?: AbortSignal) => request(
    withQuery(policiesPath(entityId), { limit: '1' }),
    (raw) => {
      if (!isRecord(raw) || !Array.isArray(raw.items)) throw new Error('Malformed whitelabel settings')
      if (requireBoolean(raw.hasMore, 'hasMore') || raw.items.length > 1)
        throw new WhitelabelMigrationRequiredError()
      return raw.items.length === 0 ? null : decodePolicy(raw.items[0])
    },
    { signal },
  ),
  get: (entityId: string, policyId: string) =>
    request(policyPath(entityId, policyId), decodePolicy),
  set: (entityId: string, settings: WhitelabelSettingsWrite) => request(
    `/api/v1/entities/${entityId}/branding`,
    (raw) => decodeSettingsResult(raw, settings.mode),
    json('PUT', settings satisfies Schemas['SetWhitelabelSettingsRequest']),
  ),
}
